import { NativeSelect, Select } from '@trm/ui-kit';
import { useBreakpoint } from '@trm/ui-kit/hooks';
import { cx } from '@trm/ui-kit/utils';
import React from 'react';
import '~/pages/auth/auth-shared.css';
import '~/pages/auth/onboarding/components/OnboardingInput/OnboardingInput.css';
import { CA_PROVINCES_MAP, US_STATES_MAP } from '~/utils/constants';

interface FormRegionSelectProps {
  label?: string;
  country: string;
  name: string;
  placeholder: string;
  value: string;
  onChange: (field: string, value: string) => void;
  onBlur: (e: React.FocusEvent<Element>) => void;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  popoverClassName?: string;
}

const FormRegionSelect: React.FC<FormRegionSelectProps> = ({
  label,
  country,
  name,
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  touched,
  disabled = false,
  className,
  inputClassName,
  popoverClassName,
}) => {
  const isDesktop = useBreakpoint('md');
  const regionMap = country === 'US' ? US_STATES_MAP : country === 'CA' ? CA_PROVINCES_MAP : {};

  const regionOptions = Object.entries(regionMap).map(([code, name]) => ({
    id: code,
    label: name,
    supportingText: `(${code})`,
  }));

  const handleSelectionChange = (key: React.Key | null) => {
    onChange(name, key ? String(key) : '');
  };

  const handleBlur = (e: React.FocusEvent<Element>) => {
    if (onBlur) {
      Object.defineProperty(e.target, 'name', {
        writable: true,
        value: name,
      });
      onBlur(e);
    }
  };

  const isDisabled = disabled || regionOptions.length === 0;

  // Use native select on mobile for better scrolling UX
  if (!isDesktop) {
    const nativeOptions = [
      { label: placeholder, value: '' },
      ...regionOptions.map((opt) => ({
        label: `${opt.label} (${opt.id})`,
        value: opt.id,
      })),
    ];

    return (
      <NativeSelect
        label={label}
        value={value}
        onChange={(event) => onChange(name, event.target.value)}
        onBlur={handleBlur}
        disabled={isDisabled}
        aria-invalid={!!(error && touched)}
        options={nativeOptions}
        hint={touched && error ? error : undefined}
        className={className}
        selectClassName={cx(
          '!rounded-[2px] !bg-[var(--auth-bg-primary-subtle)] !ring-0 !border !border-disabled',
          inputClassName,
        )}
      />
    );
  }

  return (
    <div className="onboarding-select-wrapper">
      <Select.ComboBox
        label={label}
        placeholder={placeholder}
        selectedKey={value || null}
        onSelectionChange={handleSelectionChange}
        onBlur={handleBlur}
        isDisabled={isDisabled}
        isInvalid={!!(error && touched)}
        hint={touched && error ? error : undefined}
        size="md"
        shortcut={false}
        items={regionOptions}
        inputClassName={cx(
          '!rounded-[2px] bg-[var(--auth-bg-primary-subtle)] !ring-0 border border-disabled',
          inputClassName,
        )}
        popoverClassName={cx('bg-[var(--auth-bg-primary-subtle)]', popoverClassName)}
        className={className}
      >
        {(item) => (
          <Select.Item id={item.id} supportingText={item.supportingText}>
            {item.label}
          </Select.Item>
        )}
      </Select.ComboBox>
    </div>
  );
};

export default FormRegionSelect;
